import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  AiFeaturesService,
  IQBOOST_CONVERSATION_STORE,
  IQBoostConversation,
  IQBoostViewStore,
  IQChatAddon,
  IQChatAttachment,
  IQChatFeedback,
  IQChatFrom,
  LangGraphConfirmation,
  LangGraphHandoff,
  LangGraphService,
  LangGraphThread,
  MaximizerAiService,
  readLangGraphUploads,
} from '@maximizer/ai/data-access';
import {
  ContextService,
  SessionService,
} from '@maximizer/core/shared/data-access';
import { CDN_URL } from '@maximizer/core/shared/domain';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { OpenEntityArgs } from '@maximizer/core/shared/ui/config';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import {
  KENDO_SWITCH,
  KENDO_TEXTAREA,
  TextAreaComponent,
} from '@progress/kendo-angular-inputs';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { IQBoostDocumentPreviewService } from '../../services/iqboost-document-preview.service';
import {
  EventAction,
  EventJourney,
  IQBoostEventTracker,
} from '../../classes/event-tracker';
import { AnswerContentComponent } from '../../answer-widgets/answer-content.component';
import {
  AnswerActionSummary,
  AnswerBlockSummary,
  AnswerWidgetSummary,
  summarizeAnswer,
} from '../../answer-widgets/answer-segment';
import { AnswerFollowUp } from '../follow-ups/answer-follow-ups.component';
import { IqboostAnswerFeedbackComponent } from '../iqboost-answer-feedback/iqboost-answer-feedback.component';
import { IqboostAnswerStepsComponent } from '../iqboost-answer-steps/iqboost-answer-steps.component';
import { IqboostAttachmentChipsComponent } from '../iqboost-attachment-chips/iqboost-attachment-chips.component';
import { IQBoostChatScope } from '../iqboost-chat-scope';
import {
  IQBoostConversationRename,
  IqboostConversationSidebarComponent,
} from '../iqboost-conversation-sidebar/iqboost-conversation-sidebar.component';
import {
  IQBOOST_CHAT_OVERRIDES,
  IQBOOST_DIALOG_HOST,
  IQBoostClaimedRun,
  IQBoostDialogHost,
  IQBoostOverrideView,
  IQBoostQuestion,
} from '../overrides/iqboost-chat-override';
import { IqboostOverrideViewDirective } from '../overrides/iqboost-override-view.directive';
import { IqboostSecurityStatusComponent } from '../iqboost-security-status/iqboost-security-status.component';
import { IqboostWriteConfirmationComponent } from '../iqboost-write-confirmation/iqboost-write-confirmation.component';
import {
  IQBoostStarterPrompt,
  IqboostStarterPromptsComponent,
} from '../iqboost-starter-prompts/iqboost-starter-prompts.component';
import { RICH_UI_SUFFIX, readChatHistory, untilLastQuestion } from './iqboost-chat-history';
import { IQBoostChatRun } from './iqboost-chat-run';
import { IQBoostChatPrompt, IQBoostChatRunStore } from './iqboost-chat-run.store';

// The picker's `accept` can be bypassed in the OS dialog, so the type is checked again.
const PDF_MIME_TYPE = 'application/pdf';

/** What the error dialog shows: translation keys, with the message's parameters. */
interface IQBoostChatError {
  title: string;
  message: string;
  params?: Record<string, string>;
}

const REQUEST_ERROR: IQBoostChatError = {
  title: 'iqboost.error.title',
  message: 'iqboost.error.message',
};

const TITLE_MAX_LENGTH = 100;

const toTitle = (text: string): string =>
  text.replace(/\s+/g, ' ').trim().slice(0, TITLE_MAX_LENGTH);

const isPdf = (file: File): boolean =>
  file.type === PDF_MIME_TYPE || file.name.toLowerCase().endsWith('.pdf');

const isSameFile = (a: File, b: File): boolean =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const toConversation = (thread: LangGraphThread): IQBoostConversation => {
  const title = thread.metadata?.['title'];
  const pinnedAt = thread.metadata?.['pinned_at'];
  return {
    threadId: thread.thread_id,
    title: typeof title === 'string' ? title : '',
    // A rename or a pin moves `updated_at` too; only a question moves `state_updated_at`.
    updatedAt: thread.state_updated_at ?? thread.updated_at,
    ...(thread.metadata?.['pinned'] === true
      ? { pinnedAt: typeof pinnedAt === 'string' ? pinnedAt : thread.updated_at }
      : {}),
  };
};

// The server and this client write the times in different ISO forms, so they are compared parsed.
const toConversations = (threads: LangGraphThread[]): IQBoostConversation[] =>
  [...new Map(threads.map((thread) => [thread.thread_id, thread])).values()]
    .map(toConversation)
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));

@Component({
  selector: 'ai-iqboost-chat-dialog',
  templateUrl: './iqboost-chat-dialog.component.html',
  styleUrl: './iqboost-chat-dialog.component.scss',
  // Everything the template reads is a signal, so the host's calls from outside Angular still
  // mark it; checked on every zone event instead, a window drag re-checks each answer.
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    IQBoostDocumentPreviewService,
    IQBoostEventTracker,
    { provide: IQBOOST_DIALOG_HOST, useExisting: forwardRef(() => IqboostChatDialogComponent) },
  ],
  imports: [
    AnswerContentComponent,
    IqboostAnswerFeedbackComponent,
    IqboostAnswerStepsComponent,
    IqboostAttachmentChipsComponent,
    IqboostConversationSidebarComponent,
    IqboostSecurityStatusComponent,
    IqboostStarterPromptsComponent,
    IqboostWriteConfirmationComponent,
    KENDO_BUTTON,
    KENDO_DIALOG,
    KENDO_SWITCH,
    KENDO_TEXTAREA,
    IqboostOverrideViewDirective,
    NgTemplateOutlet,
    TranslatePipe,
    UI_KENDO,
    UI_PIPES,
  ],
})
export class IqboostChatDialogComponent
  extends DisposableComponent
  implements OnInit, OnDestroy, IQBoostDialogHost
{
  private readonly context = inject(ContextService);
  private readonly sessionService = inject(SessionService);
  private readonly aiFeatures = inject(AiFeaturesService);
  private readonly maximizerAi = inject(MaximizerAiService);
  private readonly langGraph = inject(LangGraphService);
  private readonly tracker = inject(IQBoostEventTracker);
  private readonly viewStore = inject(IQBoostViewStore);
  private readonly documentPreview = inject(IQBoostDocumentPreviewService);
  // The host's store: the conversation outlives the dialog there.
  private readonly conversation = inject(IQBOOST_CONVERSATION_STORE);
  private readonly overrides = inject(IQBOOST_CHAT_OVERRIDES, { optional: true }) ?? [];
  private readonly runs = inject(IQBoostChatRunStore);
  private readonly injector = inject(Injector);
  protected readonly cdn = inject(CDN_URL);

  private readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');
  private readonly questionBox = viewChild<TextAreaComponent>('txtQuestion');

  readonly scope = input.required<IQBoostChatScope>();
  readonly scopeLabel = input('');
  readonly subtitle = input('');
  readonly placeholder = input('');
  readonly prompts = input<readonly IQBoostStarterPrompt[]>([]);
  readonly promptsLoading = input(false);
  // Asks the first of the prompts once, as soon as there is one.
  readonly autoAsk = input(false);
  // Held by the host so it survives closing the dialog, and kept by a new chat.
  readonly richUi = model(true);
  readonly closed = output<void>();
  readonly entityOpened = output<OpenEntityArgs>();

  protected readonly IQChatFrom = IQChatFrom;

  readonly chatHistory = computed(() => this.conversation.chatHistory());
  readonly conversations = computed(() => this.conversation.conversations());
  readonly threadId = computed(() => this.conversation.threadId());
  private readonly run = computed<IQBoostChatRun | undefined>(() =>
    this.runs.of(this.conversation, this.threadId()),
  );
  readonly loading = computed(() => !!this.run());
  readonly creatingThread = computed(() => this.run()?.threadId() === null);
  readonly loadingConversations = signal(false);
  readonly loadingConversation = signal(false);
  readonly streamingContent = computed(() => this.run()?.shownAnswer() ?? null);
  readonly reasoning = computed(() => this.run()?.reasoning() ?? null);
  readonly runSteps = computed(() => this.run()?.steps() ?? []);
  readonly runningSteps = computed(() => this.run()?.running() ?? []);
  readonly runHandoffs = computed(() => this.run()?.handoffs() ?? []);
  // A resumed run's confirmation already shows in the conversation, under the question it answers.
  readonly runConfirmations = computed(() => {
    const shown = new Set(
      this.chatHistory().flatMap((chat) => (chat.confirmations ?? []).map(({ id }) => id)),
    );
    return (this.run()?.confirmations() ?? []).filter(({ id }) => !shown.has(id));
  });
  // The confirmation the thread's run stopped on: nothing else can be asked on it, and no other
  // card answered, until the reader answers this one.
  protected readonly waitingOn = computed(() =>
    this.loading()
      ? undefined
      : this.chatHistory()
          .at(-1)
          ?.confirmations?.find(({ data }) => data.status === 'pending'),
  );
  readonly awaitingConfirmation = computed(() => this.waitingOn() !== undefined);
  protected readonly busyThreadIds = this.runs.busyThreadIds;
  readonly thinking = computed(
    () => this.loading() && !this.streamingContent() && this.runningSteps().length === 0,
  );
  readonly error = signal<IQBoostChatError | null>(null);
  readonly showError = computed(() => this.error() !== null);
  readonly pendingDelete = signal<IQBoostConversation | null>(null);
  // Chips until the question is sent with them; the question then shows their names.
  readonly attachments = signal<File[]>([]);

  protected readonly hasConversation = computed(
    () => this.chatHistory().length > 0 || this.loading(),
  );

  // Null leaves the conversation being read, if any, for whatever is shown next.
  private readonly conversationLoads = new Subject<string | null>();
  private autoAsked = false;
  private moduleToOpen: string | null = null;

  constructor() {
    super();

    // v2 never sets the view store's household switch, so events from the answers take the scope's.
    this.tracker.useMode(() => this.scope().mode);

    // A cited PDF opens in the document viewer, anything else in the host. Clicks while a name is
    // read are dropped, so a double click opens one viewer.
    this.addSubscription(
      this.viewStore
        .triggerOpenEntitySubject()
        .asObservable()
        .pipe(
          exhaustMap((args) =>
            this.documentPreview.open(args).pipe(
              filter((previewed) => !previewed),
              map(() => args),
            ),
          ),
        )
        .subscribe((args) => this.entityOpened.emit(args)),
    );

    this.addSubscription(
      this.conversationLoads
        .pipe(switchMap((threadId) => (threadId ? this.readConversation(threadId) : EMPTY)))
        .subscribe(),
    );

    // The host may still be loading the prompts when the dialog opens.
    effect(() => {
      const [first] = this.prompts();
      if (!first || !this.autoAsk() || this.autoAsked) {
        return;
      }
      this.autoAsked = true;
      untracked(() => {
        // A question the reader asked while the prompts loaded is not cut short.
        if (this.chatHistory().length === 0) {
          this.ask(first.text, first.promptName);
        }
      });
    });

    effect(() => {
      const threadId = this.threadId();
      untracked(() => this.overrides.forEach((override) => override.shown?.(threadId)));
    });

    effect(() => {
      const run = this.run();
      if (run) {
        run.shownAnswer();
        run.steps();
        run.running();
        untracked(() => this.scrollToBottom());
      }
    });

    this.addSubscription(
      this.runs.finished
        .pipe(filter((run) => run.owner.conversation === this.conversation))
        .subscribe((run) => {
          const failed =
            run.failed || (run.ended && isNullOrEmpty(run.answer()) && !run.waiting());
          if (failed && !run.joined && run.threadId() === this.threadId()) {
            this.error.set(REQUEST_ERROR);
          }
          // The committed copy adds the action cards and chips the streamed one held back.
          if (!run.cancelled && run.threadId() === this.threadId()) {
            this.scrollToBottom();
          }
          // Only an answer asked here and seen to its end: a cancelled run holds part of one, and
          // a joined run was asked elsewhere, under a switch this dialog cannot know.
          if (!failed && run.ended && !run.joined && !run.cancelled) {
            const { widgets, actions, followUps } = summarizeAnswer(run.answer());
            this.trackWidgets(widgets);
            this.trackActions(actions, run.threadId());
            this.trackFollowUps(followUps, run.threadId());
          }
        }),
    );
  }

  // Which widgets the model reached for and which fences it got wrong, per finished answer: the
  // only measure of whether the prompt steers it. `rich_ui` is the switch as the run ends.
  private trackWidgets({ drawn, fallbacks }: AnswerWidgetSummary): void {
    this.tracker.trackEvent(EventJourney.RichWidgets, EventAction.Shown, {
      mode: this.scope().mode,
      rich_ui: this.richUi() ? 'on' : 'off',
      widgets: drawn.join(',') || 'none',
      widget_count: String(drawn.length),
      fallbacks: fallbacks.join(',') || 'none',
      fallback_count: String(fallbacks.length),
    });
  }

  // The next steps a finished answer offered, measured against what the advisor goes on to create
  // (the cards' own `clicked` events). Answers without a block are the `rich-widgets` denominator.
  private trackActions(
    { count, types, fallback }: AnswerActionSummary,
    threadId: string | null,
  ): void {
    if (count === 0 && !fallback) {
      return;
    }
    this.tracker.trackEvent(EventJourney.RecommendedActions, EventAction.Shown, {
      mode: this.scope().mode,
      count: String(count),
      types: types.join(',') || 'none',
      fallback: fallback ? 'yes' : 'no',
      ...(threadId ? { thread_id: threadId } : {}),
    });
  }

  // The questions a finished answer offered to ask next, against the chips the reader goes on to
  // ask (`askFollowUp`). Answers without a block are the `rich-widgets` denominator.
  private trackFollowUps(
    { count, fallback }: AnswerBlockSummary,
    threadId: string | null,
  ): void {
    if (count === 0 && !fallback) {
      return;
    }
    this.tracker.trackEvent(EventJourney.FollowUps, EventAction.Shown, {
      mode: this.scope().mode,
      count: String(count),
      fallback: fallback ? 'yes' : 'no',
      ...(threadId ? { thread_id: threadId } : {}),
    });
  }

  /** A chip under the last answer asks its question as the reader would have typed it. */
  protected askFollowUp({ question, index }: AnswerFollowUp): void {
    if (!this.ask(question)) {
      return;
    }
    const threadId = this.conversation.threadId();
    this.tracker.trackEvent(EventJourney.FollowUps, EventAction.Clicked, {
      mode: this.scope().mode,
      position: String(index + 1),
      ...(threadId ? { thread_id: threadId } : {}),
    });
  }

  ngOnInit(): void {
    this.context.startDialogMode();

    // The de-anonymizer and the event tracker read the data-processing settings from the store.
    this.addSubscription(
      this.sessionService
        .getInfo()
        .pipe(
          switchMap((session) => this.aiFeatures.processFiltersConfiguration(session)),
          catchError(() => of(undefined)),
        )
        .subscribe(),
    );

    // The thread is created with the first question, so opening alone leaves nothing behind.
    this.loadConversations();
    this.focusQuestion();

    this.tracker.trackEvent(EventJourney.Action, EventAction.Opened, { mode: this.scope().mode });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.context.endDialogMode();
    this.overrides.forEach((override) => override.hidden?.());
    if (this.moduleToOpen && this.moduleToOpen !== this.context.currentModuleId) {
      // What MaxCustomAction does for ChangeCurrentModule: the CRM opens IQ Boost with
      // addTagToOpenDialog, not mountElement, so this dialog's actionCompleted may not reach it.
      top?.$page?.ModuleSwitcher?.setCurrentModule?.(this.moduleToOpen, '');
    }
  }

  close(): void {
    this.conversationLoads.next(null);
    this.resetTransientState();

    this.tracker.trackEvent(EventJourney.Action, EventAction.Closed, { mode: this.scope().mode });
    this.closed.emit();
  }

  // The dialog covers the CRM, so it closes for the module to show. The CRM switches once the
  // dialog is gone, after endDialogMode gave back the module commands startDialogMode took.
  openModule(moduleId: string): void {
    this.moduleToOpen = moduleId;
    this.close();
  }

  startNewChat(): void {
    if (this.creatingThread()) {
      return;
    }
    this.leaveConversation();
    this.conversation.startNewConversation();
    this.focusQuestion();

    this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
      name: 'new-chat',
      mode: this.scope().mode,
    });
  }

  // A conversation whose run is still at work shows as it was left; any other is read again.
  openConversation(threadId: string): void {
    if (threadId === this.conversation.threadId() || this.creatingThread()) {
      return;
    }
    this.leaveConversation();

    const left = this.runs.of(this.conversation, threadId)?.history;
    if (left) {
      this.conversation.openConversation(threadId, left);
      this.scrollToBottom();
    } else {
      this.conversationLoads.next(threadId);
    }

    this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
      name: 'open-conversation',
      mode: this.scope().mode,
    });
  }

  renameConversation({ threadId, title }: IQBoostConversationRename): void {
    const previous = this.conversation.conversations().find((item) => item.threadId === threadId);
    const newTitle = toTitle(title);
    if (!previous || isNullOrEmpty(newTitle)) {
      return;
    }
    this.conversation.renameConversation(threadId, newTitle);

    // Not tied to the dialog: closing it right after must not abort the rename.
    this.langGraph
      .renameThread(threadId, newTitle)
      .pipe(
        catchError(() => {
          this.conversation.renameConversation(threadId, previous.title);
          this.error.set(REQUEST_ERROR);
          return of(undefined);
        }),
      )
      .subscribe();

    this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
      name: 'rename-conversation',
      mode: this.scope().mode,
    });
  }

  pinConversation(threadId: string): void {
    this.savePin(threadId, new Date().toISOString());
  }

  unpinConversation(threadId: string): void {
    this.savePin(threadId, null);
  }

  requestDelete(threadId: string): void {
    const conversation = this.conversations().find((item) => item.threadId === threadId);
    this.pendingDelete.set(conversation ?? null);
  }

  cancelDelete(): void {
    this.pendingDelete.set(null);
  }

  confirmDelete(): void {
    const conversation = this.pendingDelete();
    this.pendingDelete.set(null);
    if (conversation) {
      this.deleteConversation(conversation.threadId);
    }
  }

  deleteConversation(threadId: string): void {
    this.runs.cancel(threadId);

    // Not tied to the dialog: closing it right after must not abort the delete.
    this.langGraph
      .deleteThread(threadId)
      .pipe(
        tap(() => this.conversation.removeConversation(threadId)),
        catchError(() => {
          this.error.set(REQUEST_ERROR);
          return of(undefined);
        }),
      )
      .subscribe();

    this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
      name: 'delete-conversation',
      mode: this.scope().mode,
    });
  }

  // One vote per answer, kept with the open chat rather than on the thread.
  rateAnswer(index: number, feedback: IQChatFeedback): void {
    const answer = this.conversation.chatHistory()[index];
    if (answer?.from !== IQChatFrom.Ai || answer.feedback) {
      return;
    }
    this.conversation.rateMessage(index, feedback);

    const threadId = this.conversation.threadId();
    this.tracker.trackEvent(EventJourney.Feedback, EventAction.Clicked, {
      feedback_type: feedback,
      mode: this.scope().mode,
      ...(threadId ? { thread_id: threadId } : {}),
    });
  }

  askPrompt(prompt: IQBoostStarterPrompt): void {
    if (this.ask(prompt.text, prompt.promptName)) {
      this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
        name: 'starter-prompt',
        mode: this.scope().mode,
        ...(prompt.promptName ? { prompt: prompt.promptName } : {}),
      });
    }
  }

  protected selectFiles(input: HTMLInputElement): void {
    const picked = Array.from(input.files ?? []);
    // Cleared so the same file can be picked again: the browser fires `change` only on a change.
    input.value = '';

    const pdfs = picked.filter(isPdf);
    const rejected = picked.filter((file) => !isPdf(file));
    if (rejected.length > 0) {
      this.error.set({
        title: 'iqboost.error.unsupportedFiles.title',
        message: 'iqboost.error.unsupportedFiles.message',
        params: { files: rejected.map(({ name }) => name).join(', ') },
      });
    }
    if (pdfs.length === 0) {
      return;
    }
    this.attachments.update((current) => [
      ...current,
      ...pdfs.filter(
        (file, index) =>
          !current.some((attached) => isSameFile(attached, file)) &&
          pdfs.findIndex((other) => isSameFile(other, file)) === index,
      ),
    ]);
  }

  protected removeAttachment(file: IQChatAttachment): void {
    this.attachments.update((files) => files.filter((attached) => attached !== file));
  }

  // Enter sends the question; Shift+Enter, which Angular does not match here, adds a line.
  protected submitQuestion(input: TextAreaComponent, event?: Event): void {
    if ((event as KeyboardEvent | undefined)?.isComposing) {
      return;
    }
    event?.preventDefault();
    if (this.ask(input.value ?? '')) {
      input.value = '';
    }
  }

  /**
   * False when the question is blank or the dialog is busy. A `promptName` is a pre-built
   * Langfuse prompt: its text is what is asked, while the chat shows `text`.
   */
  ask(text: string, promptName?: string): boolean {
    const question = text.trim();
    if (
      isNullOrEmpty(question) ||
      this.loading() ||
      this.loadingConversation() ||
      this.awaitingConfirmation()
    ) {
      return false;
    }

    const threadId = this.conversation.threadId();
    if (threadId) {
      this.conversation.touchConversation(threadId);
    }
    const files = this.attachments();
    // Claimed first: an override that throws must not take the reader's files with it.
    const claimed = this.claim({ text: question, files, threadId });
    this.attachments.set([]);
    const shown = (claimed?.attachments ?? files).map(({ name }) => ({ name }));
    const message = {
      content: question,
      from: IQChatFrom.User,
      ...(shown.length > 0 ? { attachments: shown } : {}),
    };
    this.conversation.appendMessage(message);
    this.tracker.trackEvent(EventJourney.AskQuestion, EventAction.MessageSent, {
      mode: this.scope().mode,
    });
    this.scrollToBottom();

    const run = new IQBoostChatRun(
      { conversation: this.conversation, overrides: this.overrides },
      { claimed, question: message },
    );
    run.threadId.set(threadId);
    run.history = this.chatHistory();
    this.runs.ask(run, {
      title: toTitle(question),
      scope: this.scope().thread,
      prompt: this.readPrompt(question, promptName, claimed, files, shown),
    });
    return true;
  }

  protected decide(confirmation: LangGraphConfirmation, approved: boolean): void {
    const threadId = this.conversation.threadId();
    if (!threadId || confirmation.id !== this.waitingOn()?.id) {
      return;
    }
    this.conversation.touchConversation(threadId);
    const question = untilLastQuestion(this.chatHistory()).at(-1);
    const run = new IQBoostChatRun(
      { conversation: this.conversation, overrides: this.overrides },
      { question: question ?? null, resumed: true },
    );
    run.threadId.set(threadId);
    run.history = this.chatHistory();
    this.runs.start(run, this.langGraph.resumeStream(threadId, { id: confirmation.id, approved }));

    this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
      name: approved ? 'confirm-changes' : 'cancel-changes',
      mode: this.scope().mode,
      thread_id: threadId,
    });
  }

  protected addonViews(addon: IQChatAddon): readonly IQBoostOverrideView[] {
    return (
      this.overrides.find((override) => override.id === addon.override)?.addonViews?.(addon) ?? []
    );
  }

  // An answer's handoffs, all in one list: a view several of them return, such as the link to the
  // Actions inbox, shows once, where the last of them put it.
  protected handoffViews(handoffs: readonly LangGraphHandoff[]): readonly IQBoostOverrideView[] {
    const views = handoffs.flatMap(
      (handoff) =>
        this.overrides
          .find((override) => override.handoffType === handoff.type)
          ?.handoffViews?.(handoff) ?? [],
    );
    return views.filter((view, index) => !views.includes(view, index + 1));
  }

  private claim(question: IQBoostQuestion): IQBoostClaimedRun | null {
    for (const override of this.overrides) {
      const claimed = override.claim?.(question);
      if (claimed) {
        return claimed;
      }
    }
    return null;
  }

  // The picker already made sure the files are PDFs, whatever type the browser reports.
  private readPrompt(
    question: string,
    promptName: string | undefined,
    claimed: IQBoostClaimedRun | null,
    files: File[],
    shown: IQChatAttachment[],
  ): Observable<IQBoostChatPrompt> {
    return forkJoin([
      readLangGraphUploads(claimed ? [...claimed.uploads] : files, PDF_MIME_TYPE),
      this.readPromptText(question, promptName),
    ]).pipe(
      map(([uploads, text]) => ({
        content: this.buildPrompt(text, claimed?.instruction),
        options: claimed
          ? { uploads, question, attachments: shown, addon: claimed.addon }
          : { uploads, question },
      })),
    );
  }

  private readConversation(threadId: string): Observable<unknown> {
    this.loadingConversation.set(true);
    return this.langGraph.getThreadConversation(threadId).pipe(
      map((thread) => readChatHistory(thread, this.overrides)),
      map((history) =>
        this.runs.of(this.conversation, threadId) ? untilLastQuestion(history) : history,
      ),
      tap((chatHistory) => {
        this.conversation.openConversation(threadId, chatHistory);
        this.scrollToBottom();
      }),
      catchError(() => {
        this.error.set(REQUEST_ERROR);
        return of(null);
      }),
      finalize(() => this.loadingConversation.set(false)),
    );
  }

  // The pinned threads however old, then the most recent. A thread still at work is joined again.
  private loadConversations(): void {
    const scope = this.scope().thread;
    this.loadingConversations.set(true);
    this.addSubscription(
      forkJoin([this.langGraph.searchPinnedThreads(scope), this.langGraph.searchThreads(scope)])
        .pipe(
          map(([pinned, recent]) => [...pinned, ...recent]),
          tap((threads) =>
            threads
              .filter(({ status }) => status === 'busy')
              .forEach(({ thread_id }) =>
                this.runs.join(
                  { conversation: this.conversation, overrides: this.overrides },
                  thread_id,
                ),
              ),
          ),
          map(toConversations),
          tap((conversations) =>
            this.conversation.setConversations(this.keepOpenConversation(conversations)),
          ),
          catchError(() => of(null)),
          finalize(() => this.loadingConversations.set(false)),
        )
        .subscribe(),
    );
  }

  // A thread created while the list was loading may be missing from it.
  private keepOpenConversation(conversations: IQBoostConversation[]): IQBoostConversation[] {
    const threadId = this.conversation.threadId();
    const open = this.conversation.conversations().find((item) => item.threadId === threadId);
    return open && !conversations.some((item) => item.threadId === threadId)
      ? [open, ...conversations]
      : conversations;
  }

  // Changed right away, and put back as it was if the server refuses.
  private savePin(threadId: string, pinnedAt: string | null): void {
    const previous = this.conversation.conversations().find((item) => item.threadId === threadId);
    if (!previous || Boolean(previous.pinnedAt) === Boolean(pinnedAt)) {
      return;
    }
    this.conversation.setConversationPin(threadId, pinnedAt);

    const request = pinnedAt
      ? this.langGraph.pinThread(threadId, pinnedAt)
      : this.langGraph.unpinThread(threadId);
    // Not tied to the dialog: closing it right after must not abort the change.
    request
      .pipe(
        catchError(() => {
          this.conversation.setConversationPin(threadId, previous.pinnedAt ?? null);
          this.error.set(REQUEST_ERROR);
          return of(undefined);
        }),
      )
      .subscribe();

    this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
      name: pinnedAt ? 'pin-conversation' : 'unpin-conversation',
      mode: this.scope().mode,
    });
  }

  // A prompt the lookup cannot find or read is asked by its title, the question the reader saw.
  private readPromptText(question: string, promptName: string | undefined): Observable<string> {
    if (!promptName) {
      return of(question);
    }
    return this.maximizerAi
      .lookupPrompts({
        project: this.aiFeatures.isSLE() ? 'IqBoostSLE' : 'IqBoost',
        filter: { name: [promptName] },
      })
      .pipe(
        map((prompts) => {
          const text = prompts.find((prompt) => prompt.name === promptName)?.prompt?.trim();
          return text || question;
        }),
        catchError(() => of(question)),
      );
  }

  // The instructions ride on the prompt, so the chat still shows the question as asked.
  private buildPrompt(text: string, overrideInstruction = ''): string {
    const { instruction } = this.scope();
    const scoped = [text, instruction, overrideInstruction].filter(Boolean).join('\n\n');
    return this.richUi() ? `${scoped}${RICH_UI_SUFFIX}` : scoped;
  }

  private resetTransientState(): void {
    this.error.set(null);
    this.pendingDelete.set(null);
  }

  // Its run goes on, and shows it again as it was left when it is shown again.
  private leaveConversation(): void {
    const run = this.run();
    if (run) {
      run.history = this.chatHistory();
    }
    this.conversationLoads.next(null);
    this.resetTransientState();
  }

  // Best effort: the question box may not be rendered yet.
  private focusQuestion(): void {
    setTimeout(() => this.questionBox()?.focus(), 0);
  }

  // After the render, not on a timer: the app is zoneless, so a timer can run before the new rows.
  private scrollToBottom(): void {
    afterNextRender(
      () => {
        const element = this.scrollContainer()?.nativeElement;
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      },
      { injector: this.injector },
    );
  }
}
